/**
 * Blog Post: Should You Make Partial Mortgage Repayments in Hong Kong? [2026 Analysis]
 * Partial Mortgage Repayment Analysis
 */
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography } from '@mui/material';
import { trackPageView } from '../utils/analytics';
import AmazonRecommendation from '../components/common/AmazonRecommendation';
import SEO from '../components/common/SEO';

export default function BlogPost2() {
  const { i18n } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';

  useEffect(() => {
    trackPageView('/blog/should-you-make-partial-mortgage-repayments-hk-2026', 'Blog Post 2');
    document.title = isEnglish
      ? 'Should You Make Partial Mortgage Repayments in Hong Kong? [2026 Analysis] | PayWise365'
      : '2026年應否提早還按揭？香港業主完整分析 | PayWise365';
  }, [isEnglish]);

  const seoTitle = isEnglish
    ? 'Should You Make Partial Mortgage Repayments in Hong Kong? [2026 Analysis] | PayWise365'
    : '2026年應否提早還按揭？香港業主完整分析 | PayWise365';

  const seoDescription = isEnglish
    ? 'Comprehensive 2026 analysis on partial mortgage repayments in Hong Kong. Learn when it makes sense to pay off your mortgage early, how rising rates affect the calculation, and strategies to save thousands in interest.'
    : '2026年香港提早還按揭完整分析。了解在加息環境下何時適合提早還款，如何計算潛在節省利息，以及最佳還款策略。';

  const seoKeywords = isEnglish
    ? 'partial mortgage repayment Hong Kong, 提早還款, early mortgage payoff, mortgage prepayment HK, interest savings, 2026 rising rates, mortgage strategy, 部分還款, 節省利息'
    : '提早還按揭, 部分還款, 香港按揭策略, 節省利息, 2026加息, 按揭提前還款, 供樓策略';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl="https://www.paywise365.com/blog/should-you-make-partial-mortgage-repayments-hk-2026"
        ogType="article"
        publishDate="2026-02-03T00:00:00+08:00"
        modifiedDate="2026-02-03T00:00:00+08:00"
        articleSection={isEnglish ? 'Mortgage Strategy' : '按揭策略'}
        tags={isEnglish ? ['Partial Repayment', 'Mortgage Strategy', 'Interest Savings'] : ['提早還款', '按揭策略', '節省利息']}
      />
      {/* Page Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
            {isEnglish ? 'Should You Make Partial Mortgage Repayments in Hong Kong?' : '2026年應否提早還按揭？香港業主完整分析'}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
            {isEnglish ? '2026 Analysis: Rising Rate Environment' : '深入分析加息環境下的最佳策略'}
          </Typography>
        </Container>
      </Box>

      {/* Article Content */}
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Box
          component="article"
          sx={{
            lineHeight: 1.8,
            '& p': {
              mb: 2,
            },
            '& h2': {
              mt: 4,
              mb: 2,
              fontSize: '1.75rem',
              fontWeight: 700,
            },
            '& h3': {
              mt: 3,
              mb: 1.5,
              fontSize: '1.5rem',
              fontWeight: 600,
            },
            '& ul, & ol': {
              mb: 2,
              pl: 3,
            },
            '& li': {
              mb: 1,
            },
          }}
        >
          {isEnglish ? (
            // English Content
            <>
              <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                  <span style={{
                    background: '#eff6ff',
                    color: '#2563eb',
                    padding: '6px 14px',
                    borderRadius: '14px',
                    fontWeight: '500',
                    marginRight: '12px'
                  }}>
                    Mortgage Strategy
                  </span>
                  <span>Published: January 31, 2026</span>
                  <span style={{ margin: '0 8px' }}>•</span>
                  <span>Reading Time: 12 minutes</span>
                </div>
              </div>

              <h2>📈 Introduction: The 2026 Rate Environment</h2>
              <p>
                In 2026, Hong Kong homeowners face a significantly different mortgage landscape than just a few years ago. With the US Federal Reserve maintaining rates at elevated levels and HIBOR hovering between 3.5-4.5%, many are asking: <strong>Should I use my savings to make partial mortgage repayments, or invest elsewhere?</strong>
              </p>
              <p>
                This isn't a simple yes-or-no question. The answer depends on your mortgage rate, investment alternatives, risk tolerance, and financial goals. In this comprehensive analysis, we'll break down the mathematics, explore real scenarios, and help you make the best decision for your situation.
              </p>

              <div style={{
                background: '#fef3c7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #fbbf24',
                margin: '30px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>💡 Quick Answer (TL;DR)</h3>
                <p><strong>Partial repayment makes sense if:</strong></p>
                <ul>
                  <li>Your mortgage rate is ≥4% and you can't find safer investments yielding more</li>
                  <li>You prioritize debt reduction and peace of mind</li>
                  <li>You have sufficient emergency funds (6-12 months expenses)</li>
                  <li>You're within the penalty-free period (no early repayment fees)</li>
                </ul>
                <p style={{ marginBottom: 0 }}><strong>Consider alternatives if:</strong></p>
                <ul style={{ marginBottom: 0 }}>
                  <li>Your mortgage rate is ≤3% (low-cost debt)</li>
                  <li>You can earn higher returns safely (e.g., 5%+ in fixed deposits)</li>
                  <li>You lack emergency savings</li>
                  <li>You're in a lock-in period with penalties</li>
                </ul>
              </div>

              <h2>💰 The Mathematics: What Are You Really Saving?</h2>

              <h3>Real Example: HKD 3,000,000 Mortgage</h3>
              <p>Let's analyze a typical Hong Kong mortgage scenario:</p>

              <div style={{
                background: '#f0f9ff',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #7dd3fc',
                margin: '20px 0'
              }}>
                <h4 style={{ marginTop: 0 }}>📊 Base Scenario</h4>
                <ul>
                  <li><strong>Outstanding Balance:</strong> HKD 3,000,000</li>
                  <li><strong>Interest Rate:</strong> 3.875% (typical 2026 rate)</li>
                  <li><strong>Remaining Term:</strong> 20 years</li>
                  <li><strong>Current Monthly Payment:</strong> HKD 18,200</li>
                  <li><strong>Total Interest (if no change):</strong> HKD 1,368,000</li>
                </ul>

                <h4>💵 Partial Repayment: HKD 500,000</h4>
                <p>You have HKD 500,000 in savings. What happens if you use it for partial repayment?</p>
              </div>

              <h3>Option A: Reduce Monthly Payment (Keep Same Term)</h3>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>Metric</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>Before</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>After</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>Savings</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Outstanding Balance</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 3,000,000</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 2,500,000</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#10b981', fontWeight: 'bold' }}>-16.7%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Monthly Payment</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 18,200</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 15,167</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#10b981', fontWeight: 'bold' }}>-HKD 3,033</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Remaining Term</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>20 years</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>20 years</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Same</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Total Interest</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 1,368,000</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 1,140,000</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#10b981', fontWeight: 'bold' }}>-HKD 228,000</td>
                    </tr>
                  </tbody>
                </table>

                <p style={{ marginTop: '15px', fontSize: '15px', fontWeight: '600', color: '#065f46' }}>
                  💰 <strong>Result:</strong> Save HKD 228,000 in interest over 20 years + reduce monthly payment by HKD 3,033
                </p>
              </div>

              <h3>Option B: Reduce Loan Term (Keep Same Payment)</h3>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>Metric</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>Before</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>After</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>Savings</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Outstanding Balance</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 3,000,000</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 2,500,000</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#10b981', fontWeight: 'bold' }}>-16.7%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Monthly Payment</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 18,200</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 18,200</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Same</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Remaining Term</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>20 years</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>15.8 years</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#10b981', fontWeight: 'bold' }}>-4.2 years</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Total Interest</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 1,368,000</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 929,000</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#10b981', fontWeight: 'bold' }}>-HKD 439,000</td>
                    </tr>
                  </tbody>
                </table>

                <p style={{ marginTop: '15px', fontSize: '15px', fontWeight: '600', color: '#065f46' }}>
                  💰 <strong>Result:</strong> Save HKD 439,000 in interest + pay off mortgage 4.2 years earlier
                </p>
              </div>

              <div style={{
                background: '#dcfce7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #10b981',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>🎯 Key Insight</h3>
                <p>
                  <strong>Option B (Reduce Term)</strong> saves you <strong>HKD 211,000 MORE</strong> than Option A! This is because you're paying down principal faster, which compounds your interest savings.
                </p>
                <p style={{ marginBottom: 0 }}>
                  <strong>Effective Return:</strong> 3.875% risk-free (your mortgage rate) + psychological benefit of being debt-free sooner.
                </p>
              </div>

              <h2>⚖️ Pros & Cons: A Balanced View</h2>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '20px',
                margin: '30px 0'
              }}>
                <div style={{
                  background: '#f0fdf4',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #86efac'
                }}>
                  <h3 style={{ marginTop: 0, color: '#065f46' }}>✅ Pros of Partial Repayment</h3>
                  <ul style={{ fontSize: '14px', lineHeight: '1.6' }}>
                    <li><strong>Guaranteed "Return":</strong> Save interest at your mortgage rate (3-4% guaranteed)</li>
                    <li><strong>Risk-Free:</strong> Unlike investments, no market risk or volatility</li>
                    <li><strong>Psychological Peace:</strong> Reduce debt burden, sleep better at night</li>
                    <li><strong>Cash Flow Improvement:</strong> Lower monthly payments (Option A)</li>
                    <li><strong>Financial Freedom Sooner:</strong> Pay off mortgage earlier (Option B)</li>
                    <li><strong>Protection Against Rate Rises:</strong> Lock in savings if rates increase further</li>
                    <li><strong>Forced Savings:</strong> Can't easily "spend" the money later</li>
                  </ul>
                </div>

                <div style={{
                  background: '#fef2f2',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #fca5a5'
                }}>
                  <h3 style={{ marginTop: 0, color: '#991b1b' }}>❌ Cons of Partial Repayment</h3>
                  <ul style={{ fontSize: '14px', lineHeight: '1.6' }}>
                    <li><strong>Opportunity Cost:</strong> Miss out on higher investment returns (if available)</li>
                    <li><strong>Loss of Liquidity:</strong> Money is locked in property, can't access easily</li>
                    <li><strong>Emergency Fund Risk:</strong> Reduced liquid savings for unexpected expenses</li>
                    <li><strong>Potential Penalties:</strong> Early repayment fees during lock-in period (1-3%)</li>
                    <li><strong>Inflation Factor:</strong> Repaying with future dollars (inflation-adjusted)</li>
                    <li><strong>Tax Efficiency:</strong> In some countries, mortgage interest is tax-deductible (not HK)</li>
                    <li><strong>Lower Credit Utilization:</strong> Might affect credit availability for other loans</li>
                  </ul>
                </div>
              </div>

              <h2>📊 The Breakeven Analysis: Repayment vs. Investment</h2>

              <p>
                The key question: <strong>Can you earn more than your mortgage rate elsewhere?</strong>
              </p>

              <h3>2026 Investment Alternatives (Hong Kong)</h3>

              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>Investment</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #2563eb' }}>Typical Return</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #2563eb' }}>Risk Level</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #2563eb' }}>Verdict</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Mortgage Repayment</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', fontWeight: 'bold' }}>3.875%</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Zero</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#10b981' }}>✅ Baseline</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>HK Savings Account</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>0.001-0.01%</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Zero</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#ef4444' }}>❌ Poor</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Fixed Deposit (12 months)</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>3.5-4.0%</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Very Low</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#f59e0b' }}>⚠️ Similar</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>HK Gov't Bonds (iBond)</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>3.0-4.5%</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Very Low</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#f59e0b' }}>⚠️ Similar</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>MPF (Pension Fund)</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>4-8% (avg)</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Medium</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#10b981' }}>✅ Better (if young)</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Stock Market (HSI)</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>6-10% (long-term)</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>High</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#10b981' }}>✅ Better (if long-term)</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Property Investment</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>3-6% (rental yield)</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>High</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#f59e0b' }}>⚠️ Depends</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>Blue Chip Dividend Stocks</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>4-6%</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>Medium</td>
                      <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', color: '#10b981' }}>✅ Potentially Better</td>
                    </tr>
                  </tbody>
                </table>

                <p style={{ fontSize: '13px', color: '#64748b', marginTop: '12px', marginBottom: 0 }}>
                  *Returns are indicative based on 2026 market conditions. Past performance doesn't guarantee future results.
                </p>
              </div>

              <h3>Decision Framework:</h3>

              <div style={{
                background: '#fffbeb',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #fcd34d',
                margin: '20px 0'
              }}>
                <h4 style={{ marginTop: 0 }}>🎯 Choose Partial Repayment If:</h4>
                <ul>
                  <li>You can't reliably earn &gt;4% elsewhere with similar risk</li>
                  <li>Your mortgage rate is ≥4%</li>
                  <li>You're risk-averse and value guaranteed returns</li>
                  <li>You're nearing retirement and want to reduce debt</li>
                  <li>You already have adequate emergency funds (6-12 months)</li>
                  <li>You have other diversified investments</li>
                </ul>

                <h4>🎯 Choose Investment If:</h4>
                <ul style={{ marginBottom: 0 }}>
                  <li>You have a long investment horizon (10+ years)</li>
                  <li>Your mortgage rate is ≤3%</li>
                  <li>You're comfortable with market volatility</li>
                  <li>You're young and can afford to take risks</li>
                  <li>You have specific investment opportunities (e.g., business, education)</li>
                  <li>You lack emergency savings (build this FIRST)</li>
                </ul>
              </div>

              <h2>🧮 Use Our Calculator: Run Your Own Numbers</h2>

              <div style={{
                background: '#dcfce7',
                padding: '25px',
                borderRadius: '12px',
                border: '2px solid #10b981',
                margin: '30px 0',
                textAlign: 'center'
              }}>
                <h3 style={{ marginTop: 0, color: '#065f46' }}>💻 Free Partial Repayment Calculator</h3>
                <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                  Don't guess! Use our free calculator to see exactly how much you'll save with your specific mortgage details.
                </p>
                <a
                  href="/"
                  style={{
                    display: 'inline-block',
                    padding: '14px 32px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                >
                  Calculate Your Savings Now →
                </a>
                <p style={{ fontSize: '13px', color: '#065f46', marginTop: '12px', marginBottom: 0 }}>
                  Takes 2 minutes • No registration required • Compare both options instantly
                </p>
              </div>

              <h2>🤔 Real-Life Scenarios: What Should You Do?</h2>

              <h3>Scenario 1: Young Professional (Age 30)</h3>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>Situation:</strong></p>
                <ul>
                  <li>Mortgage: HKD 4M at 3.75%, 25 years remaining</li>
                  <li>Savings: HKD 800K</li>
                  <li>Income: HKD 60K/month, stable job</li>
                  <li>Emergency fund: HKD 300K (5 months)</li>
                </ul>

                <p><strong>Recommendation:</strong> ⚠️ <strong>DON'T make full partial repayment</strong></p>
                <ul style={{ marginBottom: 0 }}>
                  <li>Keep HKD 400K as emergency fund (boost to 6-7 months)</li>
                  <li>Invest HKD 300K in MPF/diversified portfolio (long-term horizon)</li>
                  <li>Use HKD 100K for partial repayment (psychological benefit)</li>
                  <li><strong>Why:</strong> At 30, you have 30+ years to invest. Stock market returns (6-8%) likely beat mortgage rate long-term. But having some debt reduction provides balance.</li>
                </ul>
              </div>

              <h3>Scenario 2: Mid-Career Couple (Age 45)</h3>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>Situation:</strong></p>
                <ul>
                  <li>Mortgage: HKD 2.5M at 4.0%, 15 years remaining</li>
                  <li>Savings: HKD 1M</li>
                  <li>Household Income: HKD 100K/month</li>
                  <li>Emergency fund: HKD 600K (6 months)</li>
                  <li>MPF: HKD 2M</li>
                </ul>

                <p><strong>Recommendation:</strong> ✅ <strong>Make partial repayment of HKD 500K</strong></p>
                <ul style={{ marginBottom: 0 }}>
                  <li>Maintain HKD 600K emergency fund</li>
                  <li>Repay HKD 500K (choose Option B: reduce term to ~11 years)</li>
                  <li><strong>Result:</strong> Mortgage-free by age 56, save ~HKD 280K interest</li>
                  <li><strong>Why:</strong> Nearing retirement, reducing debt provides security. Already have MPF for retirement. Guaranteed 4% "return" is attractive at this life stage.</li>
                </ul>
              </div>

              <h3>Scenario 3: Pre-Retirement (Age 55)</h3>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>Situation:</strong></p>
                <ul>
                  <li>Mortgage: HKD 1.5M at 3.625%, 10 years remaining</li>
                  <li>Savings: HKD 2M</li>
                  <li>Income: HKD 80K/month (planning to retire in 5 years)</li>
                  <li>MPF: HKD 4M</li>
                </ul>

                <p><strong>Recommendation:</strong> ✅ <strong>PAY OFF ENTIRELY (HKD 1.5M)</strong></p>
                <ul style={{ marginBottom: 0 }}>
                  <li>Use HKD 1.5M to fully repay mortgage</li>
                  <li>Keep HKD 500K liquid</li>
                  <li><strong>Result:</strong> Debt-free before retirement, save ~HKD 300K interest, reduce monthly expenses by HKD 15K</li>
                  <li><strong>Why:</strong> At 55, capital preservation &gt; growth. Being mortgage-free in retirement dramatically reduces required income. Peace of mind is invaluable.</li>
                </ul>
              </div>

              <h2>⚠️ Important Considerations Before You Decide</h2>

              <h3>1. Emergency Fund First (Non-Negotiable)</h3>
              <p>
                <strong>Rule:</strong> NEVER make partial repayment if you don't have 6-12 months of expenses in liquid savings.
              </p>
              <p>
                <strong>Why:</strong> If you lose your job, face medical expenses, or encounter emergencies, you can't easily "withdraw" money from your mortgage. Banks won't help you. You could be forced to sell your property at a bad time.
              </p>
              <p>
                <strong>Minimum Emergency Fund:</strong> Monthly expenses × 6 months (12 months if self-employed or single income household)
              </p>

              <h3>2. Check for Early Repayment Penalties</h3>
              <p>
                Most Hong Kong mortgages have a "lock-in period" (usually 2-3 years) where early repayment incurs penalties:
              </p>
              <ul>
                <li><strong>Year 1:</strong> 2-3% of repayment amount</li>
                <li><strong>Year 2:</strong> 1-2% of repayment amount</li>
                <li><strong>Year 3:</strong> 0.5-1% of repayment amount</li>
                <li><strong>After lock-in:</strong> Usually free (some banks allow 10-20% penalty-free annually)</li>
              </ul>

              <p>
                <strong>Example:</strong> HKD 500K partial repayment in Year 1 with 2% penalty = HKD 10K fee. This reduces your savings!
              </p>

              <p>
                <strong>Action:</strong> Check your mortgage contract or call your bank to confirm penalty-free repayment limits.
              </p>

              <h3>3. Consider Your Age & Life Stage</h3>
              <p>Age matters significantly in this decision:</p>
              <ul>
                <li><strong>20s-30s:</strong> Invest more, repay less (time to recover from volatility)</li>
                <li><strong>40s:</strong> Balance approach (50/50 split)</li>
                <li><strong>50s-60s:</strong> Repay more, invest less (capital preservation, peace of mind)</li>
              </ul>

              <h3>4. Don't Forget Inflation</h3>
              <p>
                In 2026, with inflation around 2-3% annually, your "real" mortgage rate is lower than the nominal rate:
              </p>
              <ul>
                <li><strong>Nominal rate:</strong> 3.875%</li>
                <li><strong>Inflation:</strong> 2.5%</li>
                <li><strong>Real rate:</strong> ~1.375%</li>
              </ul>
              <p>
                This means you're repaying with future dollars that are worth less. This is an argument FOR keeping the mortgage (cheap debt) and investing instead.
              </p>

              <h3>5. Psychological vs. Mathematical Optimality</h3>
              <p>
                Here's the truth: <strong>The mathematically optimal decision isn't always the best decision.</strong>
              </p>
              <p>
                If being debt-free helps you sleep better, reduces stress, and improves quality of life, that has real value even if you "lose" 1-2% in potential investment returns.
              </p>
              <p>
                <strong>Money is a tool for a better life, not just a number to optimize.</strong>
              </p>

              <h2>📈 2026 Outlook: What Experts Say</h2>

              <div style={{
                background: '#f0f9ff',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #7dd3fc',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>🔮 Interest Rate Forecast (2026-2027)</h3>
                <p>
                  <strong>HSBC Forecast:</strong> HIBOR expected to remain elevated (3.5-4.5%) through 2026, with potential 0.5-1% decrease in late 2027 as US Fed begins cutting rates.
                </p>
                <p>
                  <strong>Hang Seng Bank Forecast:</strong> Rates likely peak in mid-2026, gradual decline starting Q4 2026.
                </p>
                <p>
                  <strong>Implication:</strong> If rates stay high or increase, partial repayment today looks smarter. If rates drop significantly, you might wish you'd kept the cheap 3-4% mortgage and invested instead.
                </p>
                <p style={{ marginBottom: 0 }}>
                  <strong>Bottom Line:</strong> Nobody knows for certain. Base your decision on TODAY's rates and YOUR situation, not rate predictions.
                </p>
              </div>

              <h2>✅ Decision Checklist: Should You Make Partial Repayment?</h2>

              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <p><strong>Answer these questions:</strong></p>
                <ol>
                  <li>Do I have 6-12 months emergency fund AFTER this repayment?
                    <ul>
                      <li>❌ No → Don't repay, build emergency fund first</li>
                      <li>✅ Yes → Continue to Q2</li>
                    </ul>
                  </li>
                  <li>Is my mortgage rate ≥4%?
                    <ul>
                      <li>✅ Yes → Repayment looks attractive</li>
                      <li>❌ No (≤3%) → Investment might be better</li>
                    </ul>
                  </li>
                  <li>Am I within the lock-in period with penalties?
                    <ul>
                      <li>❌ Yes → Wait until penalty-free</li>
                      <li>✅ No → Continue to Q4</li>
                    </ul>
                  </li>
                  <li>Am I nearing retirement (50+)?
                    <ul>
                      <li>✅ Yes → Strong case for repayment</li>
                      <li>❌ No → Consider investment alternative</li>
                    </ul>
                  </li>
                  <li>Do I have better investment opportunities with higher returns?
                    <ul>
                      <li>✅ Yes AND comfortable with risk → Consider investing</li>
                      <li>❌ No → Repayment makes sense</li>
                    </ul>
                  </li>
                  <li>Will being debt-free significantly reduce my stress?
                    <ul>
                      <li>✅ Yes → Do it! (Psychological benefit is real)</li>
                      <li>❌ No → Pure math decision</li>
                    </ul>
                  </li>
                </ol>

                <p style={{ marginBottom: 0, fontWeight: '600', color: '#2563eb' }}>
                  <strong>Scoring:</strong>
                </p>
                <ul style={{ marginBottom: 0 }}>
                  <li><strong>4-6 "Yes" answers:</strong> Strong case for partial repayment</li>
                  <li><strong>2-3 "Yes" answers:</strong> Consider mixed approach (partial repayment + investment)</li>
                  <li><strong>0-1 "Yes" answers:</strong> Invest instead, keep mortgage</li>
                </ul>
              </div>

              <h2>💡 Alternative Strategies (Best of Both Worlds)</h2>

              <h3>Strategy 1: Offset Mortgage Account</h3>
              <p>
                Some banks offer "mortgage offset" accounts where your savings reduce the interest charged WITHOUT actually repaying the mortgage:
              </p>
              <ul>
                <li><strong>How it works:</strong> Keep HKD 500K in offset account. You only pay interest on HKD 2.5M (instead of HKD 3M)</li>
                <li><strong>Benefit:</strong> Same interest savings BUT you keep liquidity (can withdraw if needed)</li>
                <li><strong>Trade-off:</strong> Offset account earns 0% interest</li>
                <li><strong>Availability:</strong> Limited in Hong Kong (HSBC, Hang Seng offer it)</li>
              </ul>

              <h3>Strategy 2: Overpayment Facility</h3>
              <p>
                Many banks allow regular monthly overpayments (e.g., pay HKD 20K instead of HKD 18K):
              </p>
              <ul>
                <li><strong>Benefit:</strong> Gradual debt reduction, maintains flexibility</li>
                <li><strong>Method:</strong> Automatic overpayment by HKD 2K/month = HKD 24K/year = ~1.5 years shaved off 20-year mortgage</li>
                <li><strong>Best for:</strong> Those who want debt reduction but need to maintain cash reserves</li>
              </ul>

              <h3>Strategy 3: Laddered Approach</h3>
              <ul>
                <li><strong>Year 1:</strong> Build emergency fund + invest</li>
                <li><strong>Year 2-3:</strong> Make small partial repayments (10-20% of savings annually)</li>
                <li><strong>Year 4-5:</strong> Assess and adjust based on investment performance</li>
              </ul>

              <h2>🎯 Final Recommendation</h2>

              <div style={{
                background: '#fffbeb',
                padding: '25px',
                borderRadius: '12px',
                border: '2px solid #fbbf24',
                margin: '30px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>🏆 The "Wise Money" Approach</h3>

                <p><strong>For Most Hong Kong Homeowners in 2026:</strong></p>

                <ol>
                  <li><strong>Emergency Fund First:</strong> Ensure 6-12 months of expenses in liquid savings (non-negotiable)</li>

                  <li><strong>Age-Based Allocation:</strong>
                    <ul>
                      <li><strong>Under 40:</strong> 20-30% repayment, 70-80% investment</li>
                      <li><strong>40-50:</strong> 40-50% repayment, 50-60% investment</li>
                      <li><strong>Over 50:</strong> 60-80% repayment, 20-40% investment</li>
                    </ul>
                  </li>

                  <li><strong>Rate-Based Decision:</strong>
                    <ul>
                      <li><strong>Rate &gt;4%:</strong> Favor repayment (guaranteed ~4% "return")</li>
                      <li><strong>Rate 3-4%:</strong> 50/50 split</li>
                      <li><strong>Rate &lt;3%:</strong> Favor investment (cheap debt)</li>
                    </ul>
                  </li>

                  <li><strong>Penalty Check:</strong> Never repay during lock-in period with penalties &gt;1%</li>

                  <li><strong>Review Annually:</strong> Reassess based on life changes, rate changes, investment performance</li>
                </ol>

                <p style={{ marginBottom: 0, fontWeight: '600', fontSize: '16px', color: '#92400e' }}>
                  <strong>Remember:</strong> There's no single "right" answer. The best decision balances financial optimization with personal peace of mind. If being debt-free by 55 is your goal, and you have adequate savings, DO IT. The numbers support it, and your well-being is priceless.
                </p>
              </div>

              <h2>📚 Summary: Key Takeaways</h2>

              <ol>
                <li><strong>Partial repayment offers guaranteed returns</strong> equal to your mortgage rate (3-4% in 2026)</li>
                <li><strong>Reducing loan term (Option B) saves significantly more</strong> than reducing monthly payment (Option A)</li>
                <li><strong>Emergency funds are non-negotiable</strong> - maintain 6-12 months before any repayment</li>
                <li><strong>Age matters</strong> - older homeowners benefit more from debt reduction, younger ones from investment</li>
                <li><strong>Check for penalties</strong> - early repayment fees can wipe out your savings</li>
                <li><strong>Psychology matters</strong> - peace of mind has real value beyond the numbers</li>
                <li><strong>Don't put all eggs in one basket</strong> - consider a balanced approach (partial repayment + investment)</li>
              </ol>

              <div style={{
                background: '#dcfce7',
                padding: '25px',
                borderRadius: '12px',
                border: '2px solid #10b981',
                margin: '40px 0',
                textAlign: 'center'
              }}>
                <h2 style={{ marginTop: 0, color: '#065f46' }}>🧮 Calculate Your Scenario Now</h2>
                <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                  Every mortgage is different. Use our free calculator to see your exact savings based on YOUR numbers.
                </p>
                <a
                  href="/"
                  style={{
                    display: 'inline-block',
                    padding: '14px 32px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                >
                  Try Free Calculator →
                </a>
                <p style={{ fontSize: '13px', color: '#065f46', marginTop: '12px', marginBottom: 0 }}>
                  ✓ Instant results ✓ No registration ✓ Compare both repayment options
                </p>
              </div>

              <AmazonRecommendation isEnglish={isEnglish} />

              <div style={{
                background: '#fef3c7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #fbbf24',
                margin: '30px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>⚠️ Disclaimer</h3>
                <p style={{ fontSize: '14px', marginBottom: 0 }}>
                  This article is for informational purposes only and does not constitute financial advice. Every individual's situation is unique. Consult with a licensed financial advisor before making significant financial decisions. Investment returns are not guaranteed and past performance does not predict future results. Mortgage rates and market conditions change frequently - verify current rates with your lender.
                </p>
              </div>

              <div style={{ marginTop: '50px', paddingTop: '30px', borderTop: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: '14px', color: '#64748b' }}>
                  <strong>About PayWise365:</strong><br />
                  We provide free mortgage and loan calculation tools to help Hong Kong residents make informed financial decisions. No registration required, instant results, complete privacy.
                </p>
                <p style={{ fontSize: '14px', color: '#64748b' }}>
                  <strong>Author:</strong> PayWise365 Editorial Team<br />
                  <strong>Published:</strong> January 31, 2026<br />
                  <strong>Last Updated:</strong> January 31, 2026<br />
                  <strong>Next Update:</strong> April 2026 (quarterly rate review)
                </p>
              </div>
            </>
          ) : (
            // Cantonese Content
            <>
              <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                  <span style={{
                    background: '#eff6ff',
                    color: '#2563eb',
                    padding: '6px 14px',
                    borderRadius: '14px',
                    fontWeight: '500',
                    marginRight: '12px'
                  }}>
                    按揭策略
                  </span>
                  <span>發布日期：2026年1月31日</span>
                  <span style={{ margin: '0 8px' }}>•</span>
                  <span>閱讀時間：12 分鐘</span>
                </div>
              </div>

              <div style={{
                background: '#dcfce7',
                padding: '25px',
                borderRadius: '12px',
                border: '2px solid #10b981',
                margin: '40px 0',
                textAlign: 'center'
              }}>
                <h2 style={{ marginTop: 0, color: '#065f46' }}>🧮 免費按揭計算機</h2>
                <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                  使用我們的免費計算機，即時計算提早還款可節省的利息及年期。
                </p>
                <a
                  href="/"
                  style={{
                    display: 'inline-block',
                    padding: '14px 32px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                >
                  立即計算 →
                </a>
              </div>

              <h2>📈 引言：2026年的利率環境</h2>
              <p>
                2026年，香港業主面對嘅按揭環境同幾年前已經大不相同。隨住美國聯儲局維持高息環境，香港同業拆息(HIBOR)徘徊喺3.5-4.5%之間，好多人都問：<strong>我應唔應該用儲蓄做部分按揭還款，定係將資金投資喺其他地方？</strong>
              </p>
              <p>
                呢個唔係一個簡單嘅yes or no問題。答案取決於你嘅按揭利率、其他投資選擇、風險承受能力，同埋理財目標。喺呢個全面分析入面，我哋會拆解數學計算、探討真實情況，幫你為自己做出最佳決定。
              </p>

              <div style={{
                background: '#fef3c7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #fbbf24',
                margin: '30px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>💡 快速答案 (精華版)</h3>
                <p><strong>部分還款適合你，如果：</strong></p>
                <ul>
                  <li>你嘅按揭利率 ≥4%，而且你搵唔到更高回報嘅安全投資</li>
                  <li>你優先考慮減債同埋心理安全感</li>
                  <li>你有足夠應急資金（6-12個月開支）</li>
                  <li>你喺免罰息期內（冇提早還款罰款）</li>
                </ul>
                <p style={{ marginBottom: 0 }}><strong>考慮其他選擇，如果：</strong></p>
                <ul style={{ marginBottom: 0 }}>
                  <li>你嘅按揭利率 ≤3%（低成本債務）</li>
                  <li>你可以安全咁賺取更高回報（例如：5%+ 定期存款）</li>
                  <li>你缺乏應急儲蓄</li>
                  <li>你喺鎖息期內，提早還款有罰款</li>
                </ul>
              </div>

              <h2>💰 數學計算：你實際可以慳幾多？</h2>

              <h3>實例：300萬港元按揭</h3>
              <p>讓我們分析一個典型香港按揭情況：</p>

              <div style={{
                background: '#f0f9ff',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #7dd3fc',
                margin: '20px 0'
              }}>
                <h4 style={{ marginTop: 0 }}>📊 基本情況</h4>
                <ul>
                  <li><strong>尚餘本金：</strong>HKD 3,000,000</li>
                  <li><strong>利率：</strong>3.875%（2026年典型利率）</li>
                  <li><strong>尚餘年期：</strong>20年</li>
                  <li><strong>現時每月還款額：</strong>HKD 18,200</li>
                  <li><strong>總利息（如無改變）：</strong>HKD 1,368,000</li>
                </ul>

                <h4>💵 部分還款：HKD 500,000</h4>
                <p>你有50萬港元儲蓄。如果用嚟做部分還款會點？</p>
              </div>

              <h3>選擇A：減少每月還款額（保持相同年期）</h3>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>項目</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>之前</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>之後</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>節省</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>尚餘本金</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 3,000,000</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 2,500,000</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#10b981', fontWeight: 'bold' }}>-16.7%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>每月還款</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 18,200</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 15,167</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#10b981', fontWeight: 'bold' }}>-HKD 3,033</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>還款年期</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>20年</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>20年</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>相同</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px' }}>總利息支出</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>HKD 1,368,000</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>HKD 1,140,080</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#10b981', fontWeight: 'bold', fontSize: '16px' }}>💰 慳HKD 227,920</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3>選擇B：縮短還款期（保持相同每月還款額）</h3>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>項目</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>之前</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>之後</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>節省</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>尚餘本金</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 3,000,000</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 2,500,000</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#10b981', fontWeight: 'bold' }}>-16.7%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>每月還款</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 18,200</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 18,200</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>相同</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>還款年期</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>20年（240期）</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>15.8年（190期）</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#10b981', fontWeight: 'bold' }}>⚡ 提早4.2年還清</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px' }}>總利息支出</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>HKD 1,368,000</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>HKD 956,800</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#10b981', fontWeight: 'bold', fontSize: '16px' }}>💰 慳HKD 411,200</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{
                background: '#dcfce7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #10b981',
                margin: '30px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>🎯 邊個選擇更好？</h3>
                <p><strong>選擇A（減少每月還款額）：</strong></p>
                <ul>
                  <li>✅ 即時改善每月現金流（慳HKD 3,033/月）</li>
                  <li>✅ 減輕財務壓力</li>
                  <li>✅ 適合收入唔穩定或者有其他財務目標嘅人</li>
                  <li>❌ 總利息節省較少（HKD 227,920）</li>
                </ul>
                <p><strong>選擇B（縮短還款期）：</strong></p>
                <ul style={{ marginBottom: 0 }}>
                  <li>✅ 節省最多利息（HKD 411,200）</li>
                  <li>✅ 提早4.2年還清，更快擁有物業</li>
                  <li>✅ 適合收入穩定、唔需要額外現金流嘅人</li>
                  <li>❌ 每月負擔保持不變</li>
                </ul>
              </div>

              <h2>⚖️ 部分還款 vs 其他投資選擇</h2>

              <h3>對比：用HKD 500,000做唔同用途</h3>
              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0',
                overflowX: 'auto'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #2563eb' }}>選擇</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>年回報率</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>20年後價值</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #2563eb' }}>風險</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
                        <strong>部分還款（選擇B）</strong><br />
                        <span style={{ fontSize: '12px', color: '#64748b' }}>慳利息</span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>3.875%<br /><span style={{ fontSize: '12px', color: '#64748b' }}>（相當於）</span></td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#10b981', fontWeight: 'bold' }}>慳HKD 411,200</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>
                        <span style={{ color: '#10b981' }}>● 極低</span><br />
                        <span style={{ fontSize: '12px', color: '#64748b' }}>保證回報</span>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
                        <strong>定期存款</strong><br />
                        <span style={{ fontSize: '12px', color: '#64748b' }}>2026年12個月</span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>4.0-4.5%</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 1,100,000 - 1,200,000</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>
                        <span style={{ color: '#10b981' }}>● 極低</span><br />
                        <span style={{ fontSize: '12px', color: '#64748b' }}>HKDPS保障</span>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
                        <strong>債券基金</strong><br />
                        <span style={{ fontSize: '12px', color: '#64748b' }}>投資級別</span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>4-6%</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 1,190,000 - 1,600,000</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>
                        <span style={{ color: '#fbbf24' }}>● 中低</span><br />
                        <span style={{ fontSize: '12px', color: '#64748b' }}>市場波動</span>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
                        <strong>股票指數基金</strong><br />
                        <span style={{ fontSize: '12px', color: '#64748b' }}>恆生指數ETF</span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>6-8%<br /><span style={{ fontSize: '12px', color: '#64748b' }}>（歷史平均）</span></td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>HKD 1,600,000 - 2,300,000</td>
                      <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>
                        <span style={{ color: '#ef4444' }}>● 中高</span><br />
                        <span style={{ fontSize: '12px', color: '#64748b' }}>市場波動大</span>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px' }}>
                        <strong>物業投資</strong><br />
                        <span style={{ fontSize: '12px', color: '#64748b' }}>租金回報 + 升值</span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>4-10%<br /><span style={{ fontSize: '12px', color: '#64748b' }}>（視乎地區）</span></td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>HKD 1,190,000 - 3,360,000</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        <span style={{ color: '#ef4444' }}>● 高</span><br />
                        <span style={{ fontSize: '12px', color: '#64748b' }}>流動性低、市場風險</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2>🧮 使用我們嘅計算機分析你嘅情況</h2>
              <div style={{
                background: '#dcfce7',
                padding: '25px',
                borderRadius: '12px',
                border: '2px solid #10b981',
                margin: '40px 0',
                textAlign: 'center'
              }}>
                <h3 style={{ marginTop: 0, color: '#065f46' }}>💚 免費部分還款計算機</h3>
                <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                  即時計算部分還款可以慳幾多利息。比較「減少每月還款」vs「縮短還款期」兩個選擇。
                </p>
                <a
                  href="/"
                  style={{
                    display: 'inline-block',
                    padding: '14px 32px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '16px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                >
                  開始計算 →
                </a>
              </div>

              <h2>📋 決策清單：應該做部分還款嗎？</h2>

              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>✅ 你應該考慮部分還款，如果：</h3>
                <ul>
                  <li>你嘅按揭利率 ≥ 4%</li>
                  <li>你已經有足夠應急資金（6-12個月生活費）</li>
                  <li>你冇罰息期限制，或者已經過咗鎖息期</li>
                  <li>你搵唔到風險相若但回報更高嘅投資</li>
                  <li>你優先考慮減債同埋心理安全感</li>
                  <li>你嘅風險承受能力較低</li>
                  <li>你臨近退休，想減輕債務負擔</li>
                </ul>
              </div>

              <div style={{
                background: '#fef3c7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #fbbf24',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>⚠️ 你應該考慮其他選擇，如果：</h3>
                <ul>
                  <li>你嘅按揭利率 ≤ 3%（低成本債務）</li>
                  <li>你冇足夠應急資金</li>
                  <li>你喺罰息期內，提早還款要比額外費用</li>
                  <li>你可以搵到安全投資，回報高於你嘅按揭利率</li>
                  <li>你有其他高息債務（信用卡、私人貸款）應該優先償還</li>
                  <li>你嘅年齡較輕，有更長時間承受投資風險</li>
                  <li>你需要保持資金流動性應付未來需要（例如：子女教育、創業）</li>
                </ul>
              </div>

              <h2>💡 實際案例研究</h2>

              <h3>案例1：35歲專業人士 - 選擇部分還款</h3>
              <div style={{
                background: '#f0f9ff',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #7dd3fc',
                margin: '20px 0'
              }}>
                <p><strong>背景：</strong></p>
                <ul>
                  <li>按揭餘額：HKD 4,000,000</li>
                  <li>利率：4.125%（P按）</li>
                  <li>尚餘年期：25年</li>
                  <li>每月還款：HKD 21,600</li>
                  <li>儲蓄：HKD 800,000</li>
                </ul>
                <p><strong>決定：</strong>用HKD 600,000做部分還款，保留HKD 200,000作應急</p>
                <p><strong>選擇：</strong>縮短還款期（保持每月HKD 21,600）</p>
                <p><strong>結果：</strong></p>
                <ul>
                  <li>✅ 提早6.5年還清按揭（從25年變成18.5年）</li>
                  <li>✅ 慳利息：HKD 723,000</li>
                  <li>✅ 提早無債一身輕，可以提早退休或者轉職</li>
                </ul>
                <p><strong>原因：</strong></p>
                <ul style={{ marginBottom: 0 }}>
                  <li>按揭利率4.125%已經唔低，定期存款只有4%左右</li>
                  <li>工作穩定，唔需要減輕每月負擔</li>
                  <li>想喺50歲前還清按揭</li>
                  <li>保守投資者，唔想承擔太大風險</li>
                </ul>
              </div>

              <h3>案例2：42歲企業家 - 選擇投資</h3>
              <div style={{
                background: '#fef3c7',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #fbbf24',
                margin: '20px 0'
              }}>
                <p><strong>背景：</strong></p>
                <ul>
                  <li>按揭餘額：HKD 5,000,000</li>
                  <li>利率：3.5%（H按，已鎖息）</li>
                  <li>尚餘年期：18年</li>
                  <li>每月還款：HKD 31,800</li>
                  <li>儲蓄：HKD 1,200,000</li>
                </ul>
                <p><strong>決定：</strong>唔做部分還款，將資金投資喺業務擴展</p>
                <p><strong>原因：</strong></p>
                <ul>
                  <li>按揭利率只有3.5%，相對低</li>
                  <li>業務擴展預期回報12-15%（遠高於按揭利率）</li>
                  <li>保留資金流動性對業務運作好重要</li>
                  <li>有穩定收入應付按揭還款</li>
                </ul>
                <p><strong>結果（3年後）：</strong></p>
                <ul style={{ marginBottom: 0 }}>
                  <li>✅ 業務投資帶來額外收入HKD 450,000/年</li>
                  <li>✅ 保持低成本融資，善用槓桿</li>
                  <li>✅ 3年內用業務收入再做部分還款HKD 800,000</li>
                </ul>
              </div>

              <h2>🔍 2026年特別考慮因素</h2>

              <h3>1. 利率趨勢</h3>
              <p>
                2026年香港按揭市場仍然受美國利率政策影響。如果你預期利率會繼續上升，提早還款可以鎖定現有利率嘅優勢。相反，如果你預期利率會下跌，可能要等到refinance（轉按）時先考慮大額還款。
              </p>

              <h3>2. 罰息期考慮</h3>
              <p>
                大部分香港銀行嘅按揭都有2-3年罰息期。如果你喺呢段期間做部分還款（超過每年10-20%限額），可能要支付1-3%嘅罰款。計算前記得check清楚：
              </p>
              <ul>
                <li>你嘅按揭合約罰息條款</li>
                <li>免罰息嘅還款額度（通常係每年貸款額嘅10-20%）</li>
                <li>罰息期完結日期</li>
              </ul>

              <h3>3. 稅務考慮（適用於出租物業）</h3>
              <p>
                如果你嘅物業係用嚟出租，按揭利息可以扣稅。做部分還款會減少你可以扣稅嘅利息支出。喺呢個情況下，可能保留按揭、投資其他地方反而更有利。
              </p>

              <h2>🎯 專家建議：平衡策略</h2>

              <div style={{
                background: '#eff6ff',
                padding: '25px',
                borderRadius: '12px',
                border: '2px solid #2563eb',
                margin: '30px 0'
              }}>
                <h3 style={{ marginTop: 0, color: '#1e40af' }}>💎 黃金比例方法</h3>
                <p>考慮將資金分成三部分：</p>
                <ol>
                  <li>
                    <strong>40-50% 做部分還款</strong><br />
                    <span style={{ fontSize: '14px', color: '#64748b' }}>
                      → 確保節省利息，減輕債務
                    </span>
                  </li>
                  <li>
                    <strong>30-40% 保持流動資金</strong><br />
                    <span style={{ fontSize: '14px', color: '#64748b' }}>
                      → 應急資金 + 短期投資機會（定期存款）
                    </span>
                  </li>
                  <li>
                    <strong>10-20% 較進取投資</strong><br />
                    <span style={{ fontSize: '14px', color: '#64748b' }}>
                      → 股票、債券基金（如果你接受風險）
                    </span>
                  </li>
                </ol>
                <p style={{ marginBottom: 0 }}>
                  <strong>例子：</strong>如果你有HKD 1,000,000儲蓄<br />
                  → HKD 450,000 部分還款<br />
                  → HKD 350,000 定期存款/高息儲蓄<br />
                  → HKD 200,000 債券基金/股票ETF
                </p>
              </div>

              <h2>📞 下一步行動</h2>

              <div style={{
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                margin: '20px 0'
              }}>
                <h3 style={{ marginTop: 0 }}>✅ 立即行動清單：</h3>
                <ol>
                  <li><strong>計算你嘅實際節省</strong> → 用我哋嘅<a href="/" style={{ color: '#2563eb' }}>免費計算機</a></li>
                  <li><strong>檢查按揭合約</strong> → 確認罰息期同埋還款限制</li>
                  <li><strong>評估應急資金</strong> → 確保保留6-12個月生活費</li>
                  <li><strong>比較投資選擇</strong> → 睇吓有冇更高回報嘅低風險投資</li>
                  <li><strong>聯絡銀行</strong> → 了解部分還款程序同所需文件</li>
                  <li><strong>制定還款計劃</strong> → 決定金額、時間、頻率</li>
                </ol>
              </div>

              <h2>❓ 常見問題 (FAQ)</h2>

              <div style={{ margin: '20px 0' }}>
                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q1: 我可以分多次做部分還款嗎？</h4>
                  <p style={{ marginBottom: 0 }}>
                    可以。大部分銀行容許你喺罰息期後隨時做部分還款。有啲銀行仲提供每年10-20%免罰息還款額度。不過要留意有啲銀行對每次還款嘅最低金額有要求（例如HKD 50,000）。
                  </p>
                </div>

                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q2: 做完部分還款，可以再借返出嚟嗎？</h4>
                  <p style={{ marginBottom: 0 }}>
                    唔可以直接借返，因為部分還款會減少你嘅貸款本金。如果之後需要資金，你需要重新申請「加按」或者「二按」，會當新貸款處理，要重新評估你嘅財務狀況同物業估值。所以做部分還款前一定要確保保留足夠流動資金。
                  </p>
                </div>

                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q3: 如果利率下跌，部分還款係咪就蝕咗？</h4>
                  <p style={{ marginBottom: 0 }}>
                    唔係「蝕」，而係機會成本問題。部分還款喺你做嗰刻已經開始節省利息，呢個係實際嘅財務收益。即使之後利率下跌，你仍然慳到利息，只係慳少咗啲。而且你可以喺利率下跌時考慮refinance（轉按），進一步優化你嘅按揭成本。
                  </p>
                </div>

                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q4: 我應該優先還按揭定係還信用卡數？</h4>
                  <p style={{ marginBottom: 0 }}>
                    絕對要優先還信用卡或私人貸款！呢啲高息債務（利率通常15-30%）遠高於按揭利率（3-5%）。正確優先順序：信用卡/私人貸款 → 汽車貸款 → 按揭。只有喺還清所有高息債務後先考慮部分還款。
                  </p>
                </div>

                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginTop: 0, color: '#2563eb' }}>Q5: 定期定額部分還款 vs 一次過大額還款，邊個好？</h4>
                  <p style={{ marginBottom: 0 }}>
                    <strong>數學上：</strong>一次過大額還款節省最多利息，因為越早還越慳。<br />
                    <strong>實際上：</strong>定期定額（例如每年還HKD 100,000）比較靈活，可以因應市況調整。建議：如果確定有長期穩定儲蓄，可以一次過還；如果唔肯定，可以分期還，保留彈性。
                  </p>
                </div>
              </div>

              <AmazonRecommendation isEnglish={isEnglish} />

              <div style={{ marginTop: '50px', paddingTop: '30px', borderTop: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: '14px', color: '#64748b' }}>
                  <strong>關於PayWise365：</strong><br />
                  我們提供免費的按揭及貸款計算工具，助你作出明智的財務決策。無需註冊，即時使用，保護你的私隱。
                </p>
                <p style={{ fontSize: '14px', color: '#64748b' }}>
                  <strong>作者：</strong>PayWise365 編輯團隊<br />
                  <strong>發布日期：</strong>2026年1月31日<br />
                  <strong>最後更新：</strong>2026年1月31日<br />
                  <strong>下次更新：</strong>2026年4月（按季度更新）
                </p>
              </div>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
}
